import React, { useState } from 'react';
import axios from 'axios';
import '../CrmStyles.css';
import { LuPlus, LuX, LuCircleUserRound, LuTrash2, LuStar, LuMail, LuPhone, LuSmartphone, LuPencil, LuCheck, LuShieldCheck, LuShieldAlert } from 'react-icons/lu';
import Swal from 'sweetalert2';

import BASE_URL from '../../url';

const ContactManagement = ({ companyId, contacts, onRefresh, isEditMode = false, isPortal = false }) => {
  const isPortalView = isPortal || (typeof window !== 'undefined' && window.location.pathname.startsWith('/portal'));
  const getAuthToken = () => localStorage.getItem('portal_token') || localStorage.getItem('token') || '';

  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    phone: '',
    mobile: '',
    contactRole: '',
    isSigningAuthority: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Inline row editing state
  const [editingContactId, setEditingContactId] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const [rowSaving, setRowSaving] = useState(false);

  const handleEditClick = (c) => {
    setEditingContactId(c.ContactId);
    setEditRowData({
      firstName: c.FirstName || '',
      lastName: c.LastName || '',
      jobTitle: c.JobTitle || '',
      email: c.Email || '',
      phone: c.Phone || '',
      mobile: c.Mobile || '',
      contactRole: c.ContactRole || '',
      isSigningAuthority: Boolean(c.IsSigningAuthority)
    });
  };

  const handleEditRowChange = (e) => {
    setEditRowData({ ...editRowData, [e.target.name]: e.target.value });
  };

  const handleSaveContact = async (contactId) => {
    if (!editRowData.firstName || !editRowData.lastName) {
      alert('First Name and Last Name are required.');
      return;
    }
    if (editRowData.isSigningAuthority && (!editRowData.email || !editRowData.email.trim())) {
      Swal.fire({
        icon: 'warning',
        title: 'Email Required',
        text: 'This contact must have a valid email address to be granted Signing Authority.'
      });
      return;
    }
    setRowSaving(true);
    try {
      const token = getAuthToken();
      const payload = { ...editRowData };
      if (isPortalView) {
        delete payload.isSigningAuthority;
      }
      const response = await axios.put(
        `${BASE_URL}/api/crm/companies/${companyId}/contacts/${contactId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setEditingContactId(null);
        onRefresh();
      } else {
        alert(response.data.message || 'Failed to save contact.');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving contact.');
    } finally {
      setRowSaving(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (formData.isSigningAuthority && (!formData.email || !formData.email.trim())) {
      setError('Email address is required to grant Signing Authority.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const token = getAuthToken();
      const payload = { ...formData };
      if (isPortalView) {
        payload.isSigningAuthority = false;
      }
      const response = await axios.post(`${BASE_URL}/api/crm/companies/${companyId}/contacts`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setIsAdding(false);
        setFormData({ firstName: '', lastName: '', jobTitle: '', email: '', phone: '', mobile: '', contactRole: '', isSigningAuthority: false });
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (contactId, rowVersion) => {
    try {
      const token = getAuthToken();
      await axios.put(`${BASE_URL}/api/crm/companies/${companyId}/contacts/${contactId}/primary`, 
        { rowVersion }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRefresh();
    } catch (error) {
      console.error('Error setting primary:', error);
      alert('Failed to set primary contact');
    }
  };

  const handleDelete = async (contactId, rowVersion) => {
    if (!window.confirm('Are you sure you want to deactivate this contact?')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${BASE_URL}/api/crm/companies/${companyId}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { rowVersion }
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to deactivate contact');
    }
  };

  const handleGrantSigningAuthority = async (contact) => {
    if (!contact.Email || !contact.Email.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Email Required',
        text: 'This contact must have a valid email address before being granted Signing Authority.'
      });
      return;
    }

    const confirmRes = await Swal.fire({
      title: 'Grant Signing Authority?',
      html: `Are you sure you want to authorize <b>${contact.FirstName} ${contact.LastName}</b> (${contact.Email}) as a Counterparty Signing Authority?<br/><br/><small style="color: #6b7280;">They will be authorized to access the Counterparty Portal to review and electronically sign agreements on behalf of this company.</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Grant Authority',
      confirmButtonColor: '#3b5bfd',
      cancelButtonText: 'Cancel'
    });

    if (!confirmRes.isConfirmed) return;

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${BASE_URL}/api/crm/companies/${companyId}/contacts/${contact.ContactId}/grant-signing-authority`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Authority Granted',
          text: response.data.message || 'Signing authority granted successfully.',
          timer: 2000,
          showConfirmButton: false
        });
        onRefresh();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Operation Failed',
          text: response.data.message || 'Failed to grant signing authority.'
        });
      }
    } catch (err) {
      console.error('Error granting signing authority:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to grant signing authority.'
      });
    }
  };

  const handleRevokeSigningAuthority = async (contact) => {
    const confirmRes = await Swal.fire({
      title: 'Revoke Signing Authority?',
      html: `Revoke signing authority from <b>${contact.FirstName} ${contact.LastName}</b>?<br/><br/><small style="color: #b91c1c;">Their active Counterparty Portal sessions will be terminated immediately.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Revoke',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel'
    });

    if (!confirmRes.isConfirmed) return;

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${BASE_URL}/api/crm/companies/${companyId}/contacts/${contact.ContactId}/revoke-signing-authority`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const stranded = response.data.strandedDocuments || [];
        if (stranded.length > 0) {
          const docList = stranded.map(d => `• <b>${d.DocumentNumber || d.Title}</b>`).join('<br/>');
          await Swal.fire({
            icon: 'warning',
            title: '⚠️ Stranded Documents Alert',
            html: `Signing authority was revoked, but <b>${stranded.length} pending document(s)</b> are currently awaiting signature by this contact:<br/><br/>${docList}<br/><br/>Please reassign a replacement signer in Commercial Documents.`,
            confirmButtonText: 'Understood'
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Authority Revoked',
            text: 'Signing authority has been revoked and portal sessions terminated.',
            timer: 2000,
            showConfirmButton: false
          });
        }
        onRefresh();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Operation Failed',
          text: response.data.message || 'Failed to revoke signing authority.'
        });
      }
    } catch (err) {
      console.error('Error revoking signing authority:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to revoke signing authority.'
      });
    }
  };

  return (
    <div className="crm-card">
      <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '16px', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LuCircleUserRound size={18} /> Contacts
        </h3>
        <button onClick={() => setIsAdding(!isAdding)} className={isAdding ? "crm-btn-danger" : "crm-btn-primary"} style={{ padding: '6px 12px', fontSize: '13px' }}>
          {isAdding ? <><LuX size={16} /> Cancel</> : <><LuPlus size={16} /> Add Contact</>}
        </button>
      </div>

      <div className="crm-card-body" style={{ padding: '0 24px 24px 24px' }}>
        {isAdding && (
          <form onSubmit={handleAddContact} style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', margin: '20px 0', border: '1px solid #eaeaea', display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>First Name *</label>
              <input type="text" name="firstName" className="crm-input" value={formData.firstName} onChange={handleChange} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Last Name *</label>
              <input type="text" name="lastName" className="crm-input" value={formData.lastName} onChange={handleChange} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Job Title</label>
              <input type="text" name="jobTitle" className="crm-input" value={formData.jobTitle} onChange={handleChange} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Role</label>
              <select name="contactRole" className="crm-select" value={formData.contactRole} onChange={handleChange}>
                <option value="">Select Role</option>
                <option value="PRIMARY">PRIMARY</option>
                <option value="HIRING_MANAGER">HIRING_MANAGER</option>
                <option value="BILLING">BILLING</option>
                <option value="LEGAL">LEGAL</option>
                <option value="SUPPLIER_MGR">SUPPLIER_MGR</option>
                <option value="TECHNICAL">TECHNICAL</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Email</label>
              <input type="email" name="email" className="crm-input" value={formData.email} onChange={handleChange} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Phone</label>
              <input type="text" name="phone" className="crm-input" value={formData.phone} onChange={handleChange} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Mobile</label>
              <input type="text" name="mobile" className="crm-input" value={formData.mobile} onChange={handleChange} />
            </div>
            {!isPortalView && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#4b5563' }}>Signing Authority</label>
                <div style={{ paddingTop: '2px' }}>
                  <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: formData.isSigningAuthority ? '#065f46' : '#374151',
                    background: formData.isSigningAuthority ? '#ecfdf5' : '#ffffff',
                    padding: '7px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${formData.isSigningAuthority ? '#bfdbfe' : '#d1d5db'}`,
                    transition: 'all 0.15s ease'
                  }}>
                    <input 
                      type="checkbox" 
                      name="isSigningAuthority" 
                      checked={!!formData.isSigningAuthority} 
                      onChange={(e) => setFormData({ ...formData, isSigningAuthority: e.target.checked })} 
                      style={{ width: '16px', height: '16px', accentColor: '#3b5bfd', cursor: 'pointer' }}
                    />
                    <LuShieldCheck size={16} color={formData.isSigningAuthority ? '#3b5bfd' : '#9ca3af'} />
                    <span>Authorized Signer (Portal)</span>
                  </label>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    Allows reviewing and executing MSAs/POs in Counterparty Portal
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
              <button type="submit" disabled={loading} className="crm-btn-primary">
                {loading ? 'Saving...' : 'Save Contact'}
              </button>
              {error && <span style={{ color: '#ef4444', fontSize: '14px' }}>{error}</span>}
            </div>
          </form>
        )}

        <div style={{ overflowX: 'auto', marginTop: isAdding ? '0' : '20px' }}>
          <table className="crm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Contact Info</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => {
                const isRowEditing = editingContactId === c.ContactId;
                return (
                  <tr key={c.ContactId}>
                    <td>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <input type="text" name="firstName" className="crm-input" value={editRowData.firstName} onChange={handleEditRowChange} placeholder="First Name" style={{ width: '50%' }} />
                            <input type="text" name="lastName" className="crm-input" value={editRowData.lastName} onChange={handleEditRowChange} placeholder="Last Name" style={{ width: '50%' }} />
                          </div>
                          <input type="text" name="jobTitle" className="crm-input" value={editRowData.jobTitle} onChange={handleEditRowChange} placeholder="Job Title" />
                        </div>
                      ) : (
                        <>
                          <div style={{ fontWeight: '600', color: '#111827', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span>{c.FirstName} {c.LastName}</span>
                            {c.IsPrimary ? <span className="crm-badge crm-badge-primary"><LuStar size={10} style={{ marginRight: '4px' }}/>PRIMARY</span> : null}
                            {c.IsSigningAuthority ? (
                              <span
                                style={{
                                  background: '#ecfdf5',
                                  color: '#065f46',
                                  border: '1px solid #bfdbfe',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  padding: '2px 8px',
                                  borderRadius: '12px'
                                }}
                              >
                                <LuShieldCheck size={12} color="#3b5bfd" /> SIGNING AUTHORITY
                              </span>
                            ) : null}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>{c.JobTitle}</div>
                        </>
                      )}
                    </td>
                    <td>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <select name="contactRole" className="crm-select" value={editRowData.contactRole} onChange={handleEditRowChange}>
                            <option value="">Select Role</option>
                            <option value="PRIMARY">PRIMARY</option>
                            <option value="HIRING_MANAGER">HIRING_MANAGER</option>
                            <option value="BILLING">BILLING</option>
                            <option value="LEGAL">LEGAL</option>
                            <option value="SUPPLIER_MGR">SUPPLIER_MGR</option>
                            <option value="TECHNICAL">TECHNICAL</option>
                          </select>
                          {!isPortalView && (
                            <label style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: editRowData.isSigningAuthority ? '#065f46' : '#4b5563',
                              background: editRowData.isSigningAuthority ? '#ecfdf5' : '#f9fafb',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: `1px solid ${editRowData.isSigningAuthority ? '#bfdbfe' : '#e5e7eb'}`
                            }}>
                              <input 
                                type="checkbox" 
                                name="isSigningAuthority" 
                                checked={!!editRowData.isSigningAuthority} 
                                onChange={(e) => setEditRowData({ ...editRowData, isSigningAuthority: e.target.checked })} 
                                style={{ width: '14px', height: '14px', accentColor: '#3b5bfd', cursor: 'pointer' }}
                              />
                              <LuShieldCheck size={14} color={editRowData.isSigningAuthority ? '#3b5bfd' : '#9ca3af'} />
                              <span>Signing Auth</span>
                            </label>
                          )}
                        </div>
                      ) : (
                        c.ContactRole ? <span className="crm-badge crm-badge-role">{c.ContactRole}</span> : <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={{ fontSize: '13px', color: '#4b5563' }}>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
                          <input type="email" name="email" className="crm-input" value={editRowData.email} onChange={handleEditRowChange} placeholder="Email" />
                          <input type="text" name="phone" className="crm-input" value={editRowData.phone} onChange={handleEditRowChange} placeholder="Phone" />
                          <input type="text" name="mobile" className="crm-input" value={editRowData.mobile} onChange={handleEditRowChange} placeholder="Mobile" />
                        </div>
                      ) : (
                        <>
                          {c.Email && <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}><LuMail size={14} color="#9ca3af" /> {c.Email}</div>}
                          {c.Phone && <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}><LuPhone size={14} color="#9ca3af" /> {c.Phone}</div>}
                          {c.Mobile && <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><LuSmartphone size={14} color="#9ca3af" /> {c.Mobile}</div>}
                        </>
                      )}
                    </td>
                    <td>
                      {isRowEditing ? (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={() => handleSaveContact(c.ContactId)} className="crm-btn-primary" style={{ padding: '4px 8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }} disabled={rowSaving}>
                            <LuCheck size={14} /> {rowSaving ? '...' : 'Save'}
                          </button>
                          <button onClick={() => setEditingContactId(null)} className="crm-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }} disabled={rowSaving}>
                            <LuX size={14} /> Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button onClick={() => handleEditClick(c)} style={{ background: 'none', border: 'none', color: '#3b5bfd', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} title="Edit Contact">
                            <LuPencil size={16} />
                          </button>
                          {!isPortalView && !c.IsPrimary && (
                            <button onClick={() => handleSetPrimary(c.ContactId, c.RowVersion)} className="crm-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} title="Set as primary contact">
                              Make Primary
                            </button>
                          )}
                          {!isPortalView && (
                            c.IsSigningAuthority ? (
                              <button
                                onClick={() => handleRevokeSigningAuthority(c)}
                                className="crm-btn-secondary"
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  color: '#b91c1c',
                                  borderColor: '#fca5a5',
                                  background: '#fef2f2',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title="Revoke Counterparty Portal Signing Authority"
                              >
                                <LuShieldAlert size={13} /> Revoke Signer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleGrantSigningAuthority(c)}
                                className="crm-btn-secondary"
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  color: '#065f46',
                                  borderColor: '#bfdbfe',
                                  background: '#ecfdf5',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title="Grant Counterparty Portal Signing Authority"
                              >
                                <LuShieldCheck size={13} /> + Signing Auth
                              </button>
                            )
                          )}
                          <button onClick={() => handleDelete(c.ContactId, c.RowVersion)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} title="Deactivate Contact">
                            <LuTrash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {contacts.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#6b7280' }}>No contacts found for this company.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContactManagement;
